import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTrashAlt, FaUndo } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import styled from 'styled-components';
import { archivosFiles } from '../../../../services/api';
import {
  saveNewsletterAction,
  updateNewsletterAction,
} from '../../../../store/actions';

const FormWrapper = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Section = styled.section`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.85rem 1rem 0.5rem;
  background: #fafbfc;
`;

const SectionTitle = styled.h6`
  margin: 0 0 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6c757d;
`;

const CompactLabel = styled(Label)`
  font-size: 0.78rem;
  font-weight: 800;
  color: rgb(14, 14, 14);
  margin-bottom: 0rem;
`;

const DimensionHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 72px 72px;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #868e96;

  span:first-child {
    padding-left: 0.15rem;
  }

  span {
    text-align: center;
  }
`;

const DimensionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 72px 72px;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;

  label {
    font-size: 0.78rem;
    font-weight: 500;
    margin: 0;
    color: #495057;
  }

  input {
    text-align: center;
    padding: 0.3rem 0.4rem;
    font-size: 0.85rem;
  }
`;

const ColorFieldWrap = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
`;

const ColorPicker = styled(Input)`
  width: 44px;
  min-width: 44px;
  height: 38px;
  padding: 2px;
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;
`;

const ColorPreview = styled.div`
  width: 38px;
  min-width: 38px;
  height: 38px;
  border-radius: 6px;
  border: 1px solid #ced4da;
  background: ${({ $color }) => $color || '#FFFFFF'};
  background-image: ${({ $transparent }) =>
    $transparent
      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
      : 'none'};
  background-size: ${({ $transparent }) =>
    $transparent ? '10px 10px' : 'auto'};
  background-position: ${({ $transparent }) =>
    $transparent ? '0 0, 0 5px, 5px -5px, -5px 0' : '0 0'};
  flex-shrink: 0;
`;

const UploadCard = styled.div`
  border: 1px dashed #ced4da;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PreviewBox = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  span {
    font-size: 0.75rem;
    color: #adb5bd;
  }
`;

const UploadActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.25rem;
  border-top: 1px solid #e9ecef;
`;

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

const expandShortHex = (hex) => {
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    const h = hex.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
  }
  return '#FFFFFF';
};

const toPickerHex = (value) => {
  if (!value || value.toLowerCase() === 'transparent') return '#FFFFFF';
  if (HEX_COLOR_REGEX.test(value)) return expandShortHex(value);
  return '#FFFFFF';
};

const normalizeBackgroundColor = (value) => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '#FFFFFF';
  if (trimmed.toLowerCase() === 'transparent') return 'transparent';
  if (HEX_COLOR_REGEX.test(trimmed)) return expandShortHex(trimmed);
  return trimmed;
};

const FormNewsletter = ({ isModal }) => {
  const dispatch = useDispatch();

  const tiposBoletinState = useSelector((state) => state.tiposBoletin);
  const newsletterState = useSelector((state) => state.newsletter);

  const [fileGif, setFileGif] = useState();
  const [previewGif, setPreviewGif] = useState();
  const [removeGif, setRemoveGif] = useState(false);
  const [gifInputKey, setGifInputKey] = useState(0);
  const gifInputRef = useRef(null);

  const [filePng, setFilePng] = useState();
  const [previewPng, setPreviewPng] = useState();
  const [removePng, setRemovePng] = useState(false);
  const [pngInputKey, setPngInputKey] = useState(0);
  const pngInputRef = useRef(null);

  const [link, setLink] = useState('');
  const [ancho, setAncho] = useState('85');
  const [anchoImg2, setAnchoImg2] = useState('85');
  const [anchoMovil, setAnchoMovil] = useState('90');
  const [anchoMovilImg2, setAnchoMovilImg2] = useState('90');
  const [posicion, setPosicion] = useState('1');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [paddingBottom, setPaddingBottom] = useState('3em');
  const [marginTop, setMarginTop] = useState('0px');

  const [publicar, setPublicar] = useState('1');
  const [loadingSave, setLoadingSave] = useState(false);

  const isEditing = Boolean(newsletterState.activo);
  const isTransparent = backgroundColor.trim().toLowerCase() === 'transparent';
  const pickerHex = useMemo(
    () => toPickerHex(backgroundColor),
    [backgroundColor],
  );

  const hasExistingGif = isEditing && newsletterState.activo?.gif && !removeGif;
  const hasExistingPng = isEditing && newsletterState.activo?.png && !removePng;

  const gifPreviewSrc = previewGif
    ? previewGif
    : hasExistingGif
      ? `${archivosFiles}/${newsletterState.activo.gif.folder}/${newsletterState.activo.gif.nombre}`
      : null;

  const pngPreviewSrc = previewPng
    ? previewPng
    : hasExistingPng
      ? `${archivosFiles}/${newsletterState.activo.png.folder}/${newsletterState.activo.png.nombre}`
      : null;

  const canClearGif = Boolean(fileGif || gifPreviewSrc);
  const canClearPng = Boolean(filePng || pngPreviewSrc);

  useEffect(() => {
    setPosicion(String(newsletterState.all.length + 1));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fileGif) {
      setPreviewGif(null);
      return;
    }
    const objectUrl = URL.createObjectURL(fileGif);
    setPreviewGif(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [fileGif]);

  useEffect(() => {
    if (!filePng) {
      setPreviewPng(null);
      return;
    }
    const objectUrl = URL.createObjectURL(filePng);
    setPreviewPng(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [filePng]);

  useEffect(() => {
    if (newsletterState.activo) {
      setLink(newsletterState.activo.link ?? '');
      setAncho(String(newsletterState.activo.ancho ?? '85'));
      setAnchoImg2(String(newsletterState.activo.anchoImg2 ?? '85'));
      setAnchoMovil(String(newsletterState.activo.anchoMovil ?? '90'));
      setAnchoMovilImg2(String(newsletterState.activo.anchoMovilImg2 ?? '90'));
      setPosicion(String(newsletterState.activo.posicion ?? '1'));
      setPublicar(newsletterState.activo.public ? '1' : '0');
      setBackgroundColor(newsletterState.activo.backgroundColor ?? '#FFFFFF');
      setPaddingBottom(
        newsletterState.activo.paddingBottom ??
          newsletterState.activo.heightBottom ??
          '3em',
      );
      setMarginTop(newsletterState.activo.marginTop ?? '0px');
      setFileGif(null);
      setFilePng(null);
      setRemoveGif(false);
      setRemovePng(false);
      setGifInputKey((k) => k + 1);
      setPngInputKey((k) => k + 1);
    }
  }, [newsletterState.activo]);

  const clearGifSelection = () => {
    setFileGif(null);
    setPreviewGif(null);
    setGifInputKey((k) => k + 1);
    if (gifInputRef.current) gifInputRef.current.value = '';
  };

  const clearPngSelection = () => {
    setFilePng(null);
    setPreviewPng(null);
    setPngInputKey((k) => k + 1);
    if (pngInputRef.current) pngInputRef.current.value = '';
  };

  const handleRemoveGif = () => {
    clearGifSelection();
    if (isEditing && newsletterState.activo?.gif) {
      setRemoveGif(true);
    }
  };

  const handleRemovePng = () => {
    clearPngSelection();
    if (isEditing && newsletterState.activo?.png) {
      setRemovePng(true);
    }
  };

  const handleUndoRemoveGif = () => {
    setRemoveGif(false);
    clearGifSelection();
  };

  const handleUndoRemovePng = () => {
    setRemovePng(false);
    clearPngSelection();
  };

  const handleColorPickerChange = (e) => {
    setBackgroundColor(e.target.value.toUpperCase());
  };

  const handleColorTextChange = (e) => {
    setBackgroundColor(e.target.value);
  };

  const handleSetTransparent = () => {
    setBackgroundColor('transparent');
  };

  const _onSubmitHandler = async () => {
    const data = {
      link,
      ancho: Number(ancho),
      anchoMovil: Number(anchoMovil),
      anchoImg2: Number(anchoImg2),
      anchoMovilImg2: Number(anchoMovilImg2),
      posicion: Number(posicion),
      public: publicar === '1',
      backgroundColor: normalizeBackgroundColor(backgroundColor),
      paddingBottom: paddingBottom.trim() || '3em',
      marginTop: marginTop.trim() || '0px',
      tipoBoletin: tiposBoletinState.activo.id,
    };

    setLoadingSave(true);

    if (newsletterState.activo) {
      await updateNewsletterAction(
        dispatch,
        newsletterState.activo.id,
        data,
        { tipoBoletin: tiposBoletinState.activo.id },
        fileGif,
        filePng,
        newsletterState.activo.gif?.id,
        { removeGif, removePng },
      );
    } else if (fileGif) {
      await saveNewsletterAction(dispatch, data, fileGif, filePng, {
        tipoBoletin: tiposBoletinState.activo.id,
      });
    }

    setLoadingSave(false);
    isModal(false);
  };

  return (
    <FormWrapper onSubmit={(e) => e.preventDefault()}>
      <Section>
        <SectionTitle>General</SectionTitle>
        <Row className="g-2">
          <Col md={8}>
            <FormGroup className="mb-2">
              <CompactLabel for="link">Link del video</CompactLabel>
              <Input
                id="link"
                bsSize="sm"
                name="link"
                required
                placeholder="https://..."
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup className="mb-2">
              <CompactLabel for="posicion">Posición</CompactLabel>
              <Input
                id="posicion"
                bsSize="sm"
                name="posicion"
                type="number"
                min="1"
                required
                value={posicion}
                onChange={(e) => setPosicion(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup className="mb-2">
              <CompactLabel for="tipo">Estado</CompactLabel>
              <Input
                id="tipo"
                bsSize="sm"
                name="select"
                type="select"
                required
                value={publicar}
                onChange={(e) => setPublicar(e.target.value)}
              >
                <option value="1">Activo</option>
                <option value="0">Bloqueado</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>Apariencia</SectionTitle>
        <Row className="g-2">
          <Col md={12}>
            <FormGroup className="mb-2">
              <CompactLabel for="backgroundColor">Color de fondo</CompactLabel>
              <ColorFieldWrap>
                <ColorPicker
                  id="backgroundColorPicker"
                  type="color"
                  value={pickerHex}
                  title="Seleccionar color"
                  onChange={handleColorPickerChange}
                />
                <ColorPreview
                  $color={isTransparent ? 'transparent' : backgroundColor}
                  $transparent={isTransparent}
                  title="Vista previa"
                />
                <Input
                  id="backgroundColor"
                  bsSize="sm"
                  name="backgroundColor"
                  value={backgroundColor}
                  placeholder="#FFFFFF"
                  onChange={handleColorTextChange}
                />
                <Button
                  color="light"
                  size="sm"
                  type="button"
                  onClick={handleSetTransparent}
                  active={isTransparent}
                  style={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}
                >
                  Transparente
                </Button>
              </ColorFieldWrap>
              <FormText className="mb-0">
                Selector envía formato <strong>#RRGGBB</strong>. También puedes
                escribir <code>transparent</code>.
              </FormText>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-2">
              <CompactLabel for="paddingBottom">Padding inferior</CompactLabel>
              <br />
              <label className="mb-0" style={{ fontSize: '0.72rem' }}>
                Margen interno de lado inferior del elemento
              </label>
              <InputGroup size="sm">
                <Input
                  id="paddingBottom"
                  name="paddingBottom"
                  value={paddingBottom}
                  placeholder="3em"
                  onChange={(e) => setPaddingBottom(e.target.value)}
                />
                <InputGroupText>CSS</InputGroupText>
              </InputGroup>
              <FormText className="mb-0">Ej: 3em, 2rem, 40px, 0</FormText>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-2">
              <CompactLabel for="marginTop">Margen superior</CompactLabel>
              <br />
              <label className="mb-0" style={{ fontSize: '0.72rem' }}>
                Margen externo de lado superior del elemento
              </label>
              <InputGroup size="sm">
                <Input
                  id="marginTop"
                  name="marginTop"
                  value={marginTop}
                  placeholder="0px"
                  onChange={(e) => setMarginTop(e.target.value)}
                />
                <InputGroupText>CSS</InputGroupText>
              </InputGroup>
              <FormText className="mb-0">Ej: 0px, 1em, 2rem, 40px</FormText>
            </FormGroup>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>Archivos</SectionTitle>
        <Row className="g-2">
          <Col md={6}>
            <UploadCard>
              <UploadActions>
                <CompactLabel for="portada" className="mb-0">
                  Subir imagen GIF/JPG/PNG{' '}
                  {!isEditing && <Badge color="danger">*</Badge>}
                </CompactLabel>
                <ActionGroup>
                  {fileGif && (
                    <Badge color="info" pill style={{ fontSize: '0.65rem' }}>
                      Nuevo
                    </Badge>
                  )}
                  {removeGif && (
                    <Badge color="warning" pill style={{ fontSize: '0.65rem' }}>
                      Se eliminará
                    </Badge>
                  )}
                  {removeGif && (
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      title="Deshacer eliminación"
                      onClick={handleUndoRemoveGif}
                    >
                      <FaUndo />
                    </Button>
                  )}
                  {canClearGif && !removeGif && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      type="button"
                      title={isEditing ? 'Eliminar imagen' : 'Quitar archivo'}
                      onClick={handleRemoveGif}
                    >
                      <FaTrashAlt style={{ marginRight: 4 }} />
                      {isEditing ? 'Eliminar' : 'Quitar'}
                    </Button>
                  )}
                </ActionGroup>
              </UploadActions>
              <Input
                key={gifInputKey}
                innerRef={gifInputRef}
                id="portada"
                bsSize="sm"
                name="portada"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (!e.target.files?.length) {
                    setFileGif(null);
                    return;
                  }
                  setRemoveGif(false);
                  setFileGif(e.target.files[0]);
                }}
              />
              <FormText className="mb-0">
                {isEditing
                  ? 'Puedes reemplazar, eliminar o conservar la imagen actual.'
                  : 'Archivo principal del bloque.'}
              </FormText>
              <PreviewBox>
                {gifPreviewSrc ? (
                  <img alt="Vista previa GIF/JPG" src={gifPreviewSrc} />
                ) : (
                  <span>
                    {removeGif ? 'Imagen marcada para eliminar' : 'Sin imagen'}
                  </span>
                )}
              </PreviewBox>
            </UploadCard>
          </Col>
          <Col md={6}>
            {/*
            <UploadCard>
              <UploadActions>
                <CompactLabel for="portadaPng" className="mb-0">
                  PNG overlay
                </CompactLabel>
                <ActionGroup>
                  {filePng && (
                    <Badge color="info" pill style={{ fontSize: '0.65rem' }}>
                      Nuevo
                    </Badge>
                  )}
                  {removePng && (
                    <Badge color="warning" pill style={{ fontSize: '0.65rem' }}>
                      Se eliminará
                    </Badge>
                  )}
                  {removePng && (
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      title="Deshacer eliminación"
                      onClick={handleUndoRemovePng}
                    >
                      <FaUndo />
                    </Button>
                  )}
                  {canClearPng && !removePng && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      type="button"
                      title={isEditing ? 'Eliminar PNG' : 'Quitar archivo'}
                      onClick={handleRemovePng}
                    >
                      <FaTrashAlt style={{ marginRight: 4 }} />
                      {isEditing ? 'Eliminar' : 'Quitar'}
                    </Button>
                  )}
                </ActionGroup>
              </UploadActions>
              <Input
                key={pngInputKey}
                innerRef={pngInputRef}
                id="portadaPng"
                bsSize="sm"
                name="portadaPng"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (!e.target.files?.length) {
                    setFilePng(null);
                    return;
                  }
                  setRemovePng(false);
                  setFilePng(e.target.files[0]);
                }}
              />
              <FormText className="mb-0">
                Opcional. Capa superpuesta. Puedes eliminarla al editar.
              </FormText>
              <PreviewBox>
                {pngPreviewSrc ? (
                  <img alt="Vista previa PNG" src={pngPreviewSrc} />
                ) : (
                  <span>
                    {removePng ? 'PNG marcado para eliminar' : 'Sin PNG'}
                  </span>
                )}
              </PreviewBox>
            </UploadCard>
          */}
            <SectionTitle>Dimensiones (%)</SectionTitle>
            <DimensionHeader>
              <span>Imagen</span>
              <span>Desktop</span>
              <span>Móvil</span>
            </DimensionHeader>
            <DimensionRow>
              <CompactLabel tag="label">GIF/JPG/PNG</CompactLabel>
              <Input
                bsSize="sm"
                id="ancho"
                maxLength={3}
                required
                value={ancho}
                onChange={(e) => setAncho(e.target.value.replace(/\D/g, ''))}
              />
              <Input
                bsSize="sm"
                id="anchoMovil"
                maxLength={3}
                required
                value={anchoMovil}
                onChange={(e) =>
                  setAnchoMovil(e.target.value.replace(/\D/g, ''))
                }
              />
            </DimensionRow>
          </Col>
        </Row>
      </Section>

      <Section>
        {/* <DimensionRow>
          <CompactLabel tag="label">PNG overlay</CompactLabel>
          <Input
            bsSize="sm"
            id="anchoImg2"
            maxLength={3}
            required
            value={anchoImg2}
            onChange={(e) => setAnchoImg2(e.target.value.replace(/\D/g, ''))}
          />
          <Input
            bsSize="sm"
            id="anchoMovilImg2"
            maxLength={3}
            required
            value={anchoMovilImg2}
            onChange={(e) =>
              setAnchoMovilImg2(e.target.value.replace(/\D/g, ''))
            }
          />
        </DimensionRow> */}
      </Section>

      <FormFooter>
        <Button
          color="light"
          size="sm"
          type="button"
          disabled={loadingSave}
          onClick={() => isModal(false)}
        >
          Cancelar
        </Button>
        <Button
          color={isEditing ? 'primary' : 'success'}
          size="sm"
          type="button"
          onClick={_onSubmitHandler}
          disabled={loadingSave || (!isEditing && !fileGif)}
        >
          {loadingSave ? (
            <>
              <Spinner size="sm" />
              &nbsp;Guardando...
            </>
          ) : isEditing ? (
            'Actualizar'
          ) : (
            'Guardar'
          )}
        </Button>
      </FormFooter>
    </FormWrapper>
  );
};

export default FormNewsletter;
